import { Text } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import type { RatingDistribution } from "@/types/feed";
import { View } from "react-native";

export type RatingSize = 'sm' | 'md' | 'lg';

interface SizeConfig {
  barWidth: string;
  barMargin: string;
  maxBarHeight: number;
  minBarHeight: number;
  gap: string;
  countText: string;
  labelText: string;
  labelMargin: string;
}

const SIZE_CONFIG: Record<RatingSize, SizeConfig> = {
  sm: {
    barWidth: 'w-1',
    barMargin: 'mx-px',
    maxBarHeight: 100,
    minBarHeight: 17,
    gap: 'ml-1.5',
    countText: 'text-base',
    labelText: 'text-[9px]',
    labelMargin: 'my-[-3px]',
  },
  md: {
    barWidth: 'w-1',
    barMargin: 'mx-0.5',
    maxBarHeight: 100,
    minBarHeight: 17,
    gap: 'ml-1.5',
    countText: 'text-xl',
    labelText: 'text-[10px]',
    labelMargin: 'my-[-4px]',
  },
  lg: {
    barWidth: 'w-1.5',
    barMargin: 'mx-0.5',
    maxBarHeight: 100,
    minBarHeight: 17,
    gap: 'ml-2',
    countText: 'text-2xl',
    labelText: 'text-xs',
    labelMargin: 'my-[-5px]',
  },
};

const RATING_COLORS = ["bg-muted-foreground/25", "bg-muted-foreground", "bg-primary", "bg-mustTry"];

interface RatingProps {
  distribution: RatingDistribution | null;
  size?: RatingSize;
}

function normalizedHeight(count: number, maxCount: number, maxBarHeight: number, minBarHeight: number): string {
    return maxCount > 0
    ? Math.max(minBarHeight, (count / maxCount) * maxBarHeight).toString() + '%'
    : minBarHeight.toString() + '%';
}

export function Rating({distribution, size = 'lg'}: RatingProps) {
  const counts = distribution
    ? [distribution.notForMe, distribution.okay, distribution.tryIt, distribution.mustTry]
    : [0, 0, 0, 0];

  const totalCount = counts.reduce((acc, count) => acc + count, 0);
  const maxCount = Math.max(...counts);
  const config = SIZE_CONFIG[size];

  return (
    <>
    {distribution ? (
      <View className="flex flex-row">
          <View className="flex flex-row items-end">
            {counts.map((count, i) => (
              <View
                key={i}
                className={cn(
                  config.barWidth,
                  config.barMargin,
                  RATING_COLORS[i],
                )}
                style={{ height: normalizedHeight(count, maxCount, config.maxBarHeight, config.minBarHeight) as any }}
              />
            ))}
          </View>
          <View className={cn('flex flex-col', config.gap)}>
            <Text className={cn(config.countText, 'font-bold text-foreground')}>
              {`${totalCount}`}
            </Text>
            <Text className={cn('uppercase font-semibold text-dim', config.labelMargin, config.labelText)}>
              {`${totalCount === 1 ? 'rating' : 'ratings'}`}
            </Text>
          </View>
      </View>
    ) : null}
  </>
  );
}

export default Rating;
